import { ISubmitResponse } from '@interfaces/models';
import { IOpenAccountState as _IOpenAccountState } from '@interfaces/state';
import { Action, createReducer, on } from '@ngrx/store';

import * as InternalTransferOpenAccountActions from '../../actions/open-account/internal-transfer-open-account.actions';
import * as OpenAccountActions from '../../actions/open-account/open-account.actions';
import { StartOverStateCleared } from '../../actions/start-over/start-over.actions';
import * as SubmitMetricsActions from '../../actions/submit-metrics/submit-metrics.actions';

export interface IOpenAccountState extends _IOpenAccountState {}

export const initialState: IOpenAccountState = {
  openAccountResponse: {} as ISubmitResponse,
  submitMetricsProcessingId: '',
  settlementFundChoice: null,
  isEditingProfile: false,
  isEditingEmployment: false,
  isEditingAccount: false,
  employmentMfeData: {},
  employmentMfeValidationStatus: {},
};

const openAccountReducer = createReducer(
  initialState,
  on(OpenAccountActions.submitOpenAccountResponse, (state, prop) => {
    return {
      ...state,
      openAccountResponse: prop.payload,
    };
  }),
  on(
    InternalTransferOpenAccountActions.submitInternalTransferOpenAccountResponse,
    (state, prop) => {
      return {
        ...state,
        openAccountResponse: prop.payload,
      };
    },
  ),
  on(SubmitMetricsActions.submitMetricUpdateProcessingId, (state, prop) => {
    return {
      ...state,
      submitMetricsProcessingId: prop.payload.correlationId,
    };
  }),
  on(OpenAccountActions.updateSettlementFundChoice, (state, prop) => {
    return {
      ...state,
      settlementFundChoice: prop.payload,
    };
  }),
  on(OpenAccountActions.setIsEditingProfile, (state, { isEditing }) => ({
    ...state,
    isEditingProfile: isEditing,
  })),
  on(OpenAccountActions.setIsEditingEmployment, (state, { isEditing }) => ({
    ...state,
    isEditingEmployment: isEditing,
  })),
  on(OpenAccountActions.setIsEditingAccount, (state, { isEditing }) => ({
    ...state,
    isEditingAccount: isEditing,
  })),
  on(OpenAccountActions.updateEmploymentMfeData, (state, { ownerIndex, employment }) => ({
    ...state,
    employmentMfeData: {
      ...state.employmentMfeData,
      [ownerIndex]: employment,
    },
  })),
  on(
    OpenAccountActions.updateEmploymentMfeValidationStatus,
    (state, { ownerIndex, validationStatus }) => ({
      ...state,
      employmentMfeValidationStatus: {
        ...state.employmentMfeValidationStatus,
        [ownerIndex]: validationStatus,
      },
    }),
  ),
  on(OpenAccountActions.clearEmploymentMfeData, (state, { ownerIndex }) => {
    if (ownerIndex !== undefined) {
      const { [ownerIndex]: _removedData, ...remainingData } = state.employmentMfeData;
      const { [ownerIndex]: _removedStatus, ...remainingStatus } =
        state.employmentMfeValidationStatus;
      return {
        ...state,
        employmentMfeData: remainingData,
        employmentMfeValidationStatus: remainingStatus,
      };
    }
    return {
      ...state,
      employmentMfeData: {},
      employmentMfeValidationStatus: {},
    };
  }),
  on(OpenAccountActions.clearOpenAccountState, () => ({ ...initialState })),
  on(StartOverStateCleared, () => ({ ...initialState })),
);

export function reducer(state: IOpenAccountState | undefined, action: Action) {
  return openAccountReducer(state, action);
}

export const getOpenAccountResponse = (state: IOpenAccountState) => state.openAccountResponse;

export const getSubmitMetricsID = (state: IOpenAccountState) => state.submitMetricsProcessingId;

export const getSettlementFundChoice = (state: IOpenAccountState) => state.settlementFundChoice;

export const getIsEditingProfile = (state: IOpenAccountState) => state.isEditingProfile;

export const getIsEditingEmployment = (state: IOpenAccountState) => state.isEditingEmployment;

export const getIsEditingAccount = (state: IOpenAccountState) => state.isEditingAccount;

export const getEmploymentMfeData = (state: IOpenAccountState) => state.employmentMfeData;

export const getEmploymentMfeValidationStatus = (state: IOpenAccountState) =>
  state.employmentMfeValidationStatus;
